import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import { createServerFn, useServerFn } from "@tanstack/react-start";
import { Headphones, ShieldCheck, Sparkles } from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { SignOutButton } from "@/components/site/AccountMenu";


import { requireSupabaseAuth } from "@/lib/require-auth";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { listMyMayaGuides, type MyMayaGuideItem } from "@/lib/maya-access.functions";

const getMyBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("custom_bookings")
      .select("id, booking_code, tour_title, travel_date, travelers, currency, total, status, source, bokun_confirmation_code, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

const myBookingsQuery = queryOptions({
  queryKey: ["my-bookings"],
  queryFn: async () => {
    try {
      return await getMyBookings();
    } catch (err) {
      // Sesión aún no lista o expirada: no tumbar la página.
      if (err instanceof Error && err.message.startsWith("Unauthorized")) {
        return { bookings: [] as Awaited<ReturnType<typeof getMyBookings>>["bookings"] };
      }
      throw err;
    }
  },
  staleTime: 30_000,
});

export const Route = createFileRoute("/_authenticated/mi-cuenta")({
  head: () => ({ meta: [{ title: "Mi cuenta — Mostrando México" }, { name: "robots", content: "noindex,nofollow" }] }),
  loader: ({ context }) => context.queryClient.ensureQueryData(myBookingsQuery),
  component: MyAccountPage,
});

// Aviso neutro cuando se llega desde una ruta restringida; no revela nada del panel.
function NoAccessNotice() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("aviso") === "sin-acceso") {
      setShow(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);
  if (!show) return null;
  return (
    <p role="status" className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-sm">
      Esa página no está disponible para tu cuenta. Aquí tienes tus reservas.
    </p>
  );
}

function MyAccountPage() {
  const { isAdmin, isOwner, roles } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Mi cuenta</h1>
            <p className="mt-1 text-sm text-muted-foreground">Tus reservas y guías Maya.</p>
          </div>
          <SignOutButton />
        </div>
        <NoAccessNotice />

        {isAdmin && (
          <Link
            to="/admin/dashboard"
            className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-primary/40 bg-primary/5 p-5 transition hover:border-primary hover:bg-primary/10"
          >
            <span className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-semibold">Administración</span>
                <span className="block text-xs text-muted-foreground">
                  Panel, tours, reservas, clientes, catálogo, Bokun y Travelpayouts
                </span>
              </span>
            </span>
            <Badge variant="secondary" className="uppercase">
              {isOwner ? "Owner" : "Admin"}
            </Badge>
          </Link>
        )}
        {!isAdmin && roles.includes("moderator") && (
          <Link
            to="/admin/reviews"
            className="mt-6 flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-primary/50"
          >
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span className="font-semibold">Moderación de reseñas</span>
          </Link>
        )}


        <section className="mt-8">
          <h2 className="mb-4 text-lg font-semibold">Mis guías</h2>
          <Suspense fallback={<Skeleton className="h-32 w-full" />}>
            <MyGuidesList />
          </Suspense>
        </section>

        <section className="mt-10">
          <h2 className="mb-4 text-lg font-semibold">Mis reservas</h2>
          <Suspense fallback={<Skeleton className="h-40 w-full" />}>
            <BookingList />
          </Suspense>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function MyGuidesList() {
  const fetchGuides = useServerFn(listMyMayaGuides);
  const { data } = useSuspenseQuery({
    queryKey: ["my-maya-guides"],
    // Si la sesión aún no está lista o expiró, no tumbamos la página entera:
    // mostramos la lista vacía y el vigilante de sesión redirige si hace falta.
    queryFn: async () => {
      try {
        return await fetchGuides();
      } catch (err) {
        if (err instanceof Error && err.message.startsWith("Unauthorized")) {
          return [] as MyMayaGuideItem[];
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
  if (!data.length) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
        Aún no tienes guías Maya. Explora los tours para descubrir guías con narración IA.
      </div>
    );
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {data.map((g: MyMayaGuideItem) => {
        const title =
          g.tourTitle ??
          (g.destinationKeys[0] ? g.destinationKeys.join(", ") : "Maya Premium");
        const disabled = g.status !== "active";
        const inner = (
          <div
            className={`h-full rounded-2xl border bg-card p-4 transition ${
              disabled ? "opacity-60" : "hover:border-primary/50 hover:shadow-sm"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                  {g.tourSlug ? <Headphones className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                </div>
                <div className="font-semibold leading-tight">{title}</div>
              </div>
              <GuideStatusBadge status={g.status} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>
                {g.source === "grant" ? "Cortesía" : "Compra"}
                {" · "}
                {new Date(g.grantedAt).toLocaleDateString("es-MX", { dateStyle: "medium" })}
              </span>
              {g.language && <span className="uppercase">{g.language}</span>}
              {g.expiresAt && (
                <span>
                  Vence:{" "}
                  {new Date(g.expiresAt).toLocaleDateString("es-MX", { dateStyle: "medium" })}
                </span>
              )}
            </div>
          </div>
        );
        if (g.tourSlug && !disabled) {
          return (
            <li key={g.id}>
              <Link to="/tour/$slug" params={{ slug: g.tourSlug }} className="block h-full">
                {inner}
              </Link>
            </li>
          );
        }
        if (!disabled) {
          return (
            <li key={g.id}>
              <Link to="/maya" className="block h-full">
                {inner}
              </Link>
            </li>
          );
        }
        return <li key={g.id}>{inner}</li>;
      })}
    </ul>
  );
}

function GuideStatusBadge({ status }: { status: MyMayaGuideItem["status"] }) {
  if (status === "active") return <Badge variant="default">Disponible</Badge>;
  if (status === "expired") return <Badge variant="outline">Expirada</Badge>;
  return <Badge variant="destructive">Revocada</Badge>;
}

function BookingList() {
  const { data } = useSuspenseQuery(myBookingsQuery);
  if (!data.bookings.length) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Aún no tienes reservas.{" "}
        <Link to="/tours" className="font-medium text-primary underline">
          Explora tours
        </Link>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {data.bookings.map((b) => (
        <li key={b.id}>
          <Link
            to="/booking/$code"
            params={{ code: b.booking_code }}
            className="block rounded-lg border bg-card p-5 transition hover:border-primary/50 hover:shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-semibold">{b.tour_title}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {b.travel_date} · {b.travelers} pax · {b.booking_code}
                </div>
              </div>
              <div className="text-right">
                <StatusBadge status={b.status} />
                <div className="mt-1 text-sm font-semibold">
                  {Number(b.total).toFixed(2)} {b.currency}
                </div>
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: "Pendiente", variant: "outline" },
    paid: { label: "Pagada", variant: "secondary" },
    confirmed: { label: "Confirmada", variant: "default" },
    completed: { label: "Completada", variant: "default" },
    cancelled: { label: "Cancelada", variant: "destructive" },
    failed: { label: "Pago fallido", variant: "destructive" },
  };
  const s = map[status] ?? { label: status, variant: "outline" as const };
  return <Badge variant={s.variant}>{s.label}</Badge>;
}
